import type { ConfirmedExpReadout, ExpExactCandidate, ExpReadoutReason, ExpReadoutSnapshot, ExpRecoveryMetric, GameEvent, GameStateSnapshot, ValidatedObservation } from '@/domain/types'
import { SessionExpCounter } from './session-exp-counter'
import { SessionStartExpConsensus } from './session-start-baseline'

const HOUR_MS = 3_600_000
const RECOVERY_WARMUP_MS = 10_000

/** Presentation state is session-owned and cannot alter the accounting counter. */
export class SessionExpReadout {
  private holdMs = 15_000
  private lastConfirmed: ConfirmedExpReadout | null = null
  private sessionConfirmed: ConfirmedExpReadout | null = null
  private recovering = false
  private boundary = -Infinity
  private gapReason: ExpReadoutReason | null = null
  private recovery = new SessionExpCounter()
  private consensus: SessionStartExpConsensus | null = null
  private startedAt: number | null = null
  private baselineActiveAt = 0
  private lastBaselineSampleAt: number | null = null
  private since: number | null = null
  private previousStatus: ExpReadoutSnapshot['status'] = 'ready'

  setSamplingInterval(intervalMs: number): void {
    if (Number.isFinite(intervalMs) && intervalMs > 0) this.holdMs = Math.max(15_000, intervalMs * 3)
  }

  reset(): void {
    this.lastConfirmed = this.sessionConfirmed = null
    this.recovering = false
    this.boundary = -Infinity
    this.gapReason = null
    this.resetRecovery()
    this.since = null
    this.previousStatus = 'ready'
  }

  breakContinuity(at: number): void {
    this.boundary = at
    this.gapReason = 'continuity-break'
    this.resetRecovery()
  }

  private resetRecovery(): void {
    this.recovery.reset()
    this.consensus = null
    this.startedAt = this.lastBaselineSampleAt = null
    this.baselineActiveAt = 0
    this.lastConfirmed = null
  }

  ingest(main: SessionExpCounter, state: GameStateSnapshot, observation: ValidatedObservation | undefined,
    events: readonly GameEvent[], minimumConfidence: number, activeAt: (sourceAt: number) => number): void {
    const now = state.capturedAt
    if (!main.isIncomplete()) {
      this.gapReason = null
      this.remember(main, activeAt)
      this.sessionConfirmed = this.lastConfirmed
      return
    }
    if (!this.recovering) {
      this.recovering = true
      this.boundary = Math.max(this.boundary, main.incompleteSince() ?? now)
      this.gapReason ??= 'confirmation-timeout'
      this.resetRecovery()
    }
    this.recovery.expireJourney(now)
    const lastAt = this.recovery.latestSourceAt() ?? this.lastBaselineSampleAt
    if (this.recovery.isIncomplete() || lastAt !== null && now - lastAt > this.holdMs) {
      this.breakContinuity(now)
    }
    if (this.startedAt === null) {
      // Two newly accepted endpoints with a refreshed, matching level establish a new origin.
      const candidate = observation && this.baselineCandidate(observation, minimumConfidence)
      if (!candidate) return
      this.consensus ??= new SessionStartExpConsensus(this.boundary, minimumConfidence)
      const result = this.consensus.observe(candidate)
      if (result.status === 'ignored') return
      this.lastBaselineSampleAt = candidate.sourceAt
      if (!result.confirmed) return
      const confirmed = result.confirmed
      this.startedAt = confirmed.sourceAt
      this.baselineActiveAt = activeAt(confirmed.sourceAt)
      this.recovery.reset({ ...state, capturedAt: confirmed.sourceAt, level: confirmed.level,
        exp: confirmed.value, observedAt: { ...state.observedAt!, exp: confirmed.sourceAt, level: confirmed.levelSourceAt },
        confidence: { ...state.confidence, exp: confirmed.confidence } }, confirmed.sourceAt)
      return
    }
    if (observation) this.recovery.ingestObservation(observation, minimumConfidence)
    this.recovery.confirmLevelUp(events)
    this.remember(this.recovery, at => activeAt(at) - this.baselineActiveAt, RECOVERY_WARMUP_MS)
  }

  private baselineCandidate(observation: ValidatedObservation, minimumConfidence: number): ExpExactCandidate | null {
    const level = observation.level
    if (!observation.accepted.level || level.value === null || !Number.isFinite(level.confidence) || level.confidence < minimumConfidence
      || !Number.isFinite(level.capturedAt)
      || level.capturedAt <= this.boundary || level.capturedAt > observation.capturedAt
      || observation.capturedAt - level.capturedAt > this.holdMs) return null
    const evidence = observation.expExactEvidenceAccepted ? observation.expExactEvidence : undefined
    // accepted.exp refers to the published reading, not an independently pending candidate.
    const source = evidence ?? (observation.expCandidateAdmission?.status === 'accepted' ? observation.expExactCandidate : undefined)
    const value = source?.value ?? (observation.accepted.exp ? observation.exp.value : null)
    const sourceAt = source?.sourceAt ?? observation.exp.capturedAt
    if (!value || value.estimated || value.absolute == null || value.ratio == null
      || !Number.isFinite(sourceAt) || sourceAt <= this.boundary || sourceAt > observation.capturedAt || observation.capturedAt - sourceAt > this.holdMs
      || source && source.level !== level.value) return null
    return { value: { ...value, absolute: value.absolute, ratio: value.ratio }, level: level.value,
      levelSourceAt: level.capturedAt, sourceAt, deliveredAt: observation.capturedAt,
      confidence: source?.confidence ?? observation.exp.confidence,
      percentagePrecision: source?.percentagePrecision ?? value.percentagePrecision ?? 2,
      barConsistency: 'unavailable', recognitionPath: source && 'recognitionPath' in source ? source.recognitionPath : 'legacy' }
  }

  private remember(counter: SessionExpCounter, activeAt: (at: number) => number, minimumDuration = 0): void {
    const display = counter.snapshot()
    if (display.status !== 'ready' || display.netGain === null || !display.point || display.sourceAt === null) return
    const duration = activeAt(display.sourceAt)
    if (duration < minimumDuration) return
    const value = duration > 0 ? display.netGain / duration * HOUR_MS : 0
    if (Number.isFinite(value)) this.lastConfirmed = { value, netGain: display.netGain, sourceAt: display.sourceAt, point: display.point }
  }

  snapshot(main: SessionExpCounter, now: number, running: boolean, activeDurationMs: number): {
    expReadout: ExpReadoutSnapshot; expRecovery?: ExpRecoveryMetric
  } {
    const incomplete = main.isIncomplete()
    const counter = incomplete ? this.recovery : main
    const display = counter.snapshot()
    const cached = incomplete ? (this.recovering ? this.lastConfirmed : null) : this.sessionConfirmed
    const fresh = cached !== null && now - cached.sourceAt <= this.holdMs
    const ready = cached !== null && display.status === 'ready' && (fresh || !running)
    const status = incomplete && !cached ? 'recovering' : ready ? 'ready' : fresh ? 'updating' : 'stale'
    const reason = incomplete ? counter.reviewReason() ?? this.gapReason ?? 'confirmation-timeout'
      : status === 'ready' ? null : main.reviewReason() ?? 'source-unreadable'
    if (status !== this.previousStatus) {
      if (this.previousStatus === 'ready' || status === 'ready') this.since = status === 'ready' ? null : now
      this.previousStatus = status
    }
    const duration = this.startedAt === null ? 0 : Math.max(0, activeDurationMs - this.baselineActiveAt)
    return {
      expReadout: { status, scope: incomplete ? 'recovery' : 'session', incomplete, reason, since: this.since,
        holdMs: this.holdMs, lastConfirmed: cached },
      ...(incomplete ? { expRecovery: { value: ready && display.netGain !== null && duration > 0 ? display.netGain / duration * HOUR_MS : null,
        status: ready ? 'ready' : 'unavailable', calculationStatus: display.status, updatedAt: cached?.sourceAt ?? null,
        startedAt: this.startedAt, activeDurationMs: duration, netGain: ready ? display.netGain : null,
        approximate: display.approximate } as ExpRecoveryMetric } : {}),
    }
  }
}
